import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/blogs", label: "Blogs", id: "nav-blogs-link" },
  { to: "/premium", label: "Become Pro", id: "nav-premium-link" },
  { to: "/careers", label: "Careers", id: "nav-careers-link" },
  { to: "/privacy-policy", label: "Privacy Policy", id: "nav-privacy-link" },
  { to: "/help", label: "Help", id: "nav-help-link" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-40 pt-3 sm:pt-5 transition-all duration-500 ${scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5 shadow-[0_10px_40px_-25px_rgba(15,23,42,0.35)] !pt-1" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[84px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="TruCitizen home">
          <img src="/logo.png" alt="TruCitizen" className="h-[58px] sm:h-[72px] w-auto object-contain transition-transform duration-500 hover:scale-[1.03]" />
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.id} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <button className="md:hidden p-2 rounded-full hover:bg-black/5" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white/90 backdrop-blur-xl border-b border-black/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} className="nav-link text-lg py-3 border-b border-black/[0.06] last:border-0" data-testid={`${l.id}-mobile`}>
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
